import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Loader2, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  backfillFeed,
  getFeedStats,
  listOutbox,
  retryFeedRows,
  runFeedSenderNow,
} from "@/lib/rpc/feed.functions";

const STATUSES = ["all", "queued", "sending", "sent", "failed", "dead"] as const;

function fmt(ts: string | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
}

export function FeedStatusCard() {
  const qc = useQueryClient();
  const stats = useServerFn(getFeedStats);
  const list = useServerFn(listOutbox);
  const backfill = useServerFn(backfillFeed);
  const runNow = useServerFn(runFeedSenderNow);
  const retry = useServerFn(retryFeedRows);

  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const statsQ = useQuery({
    queryKey: ["feed-stats"],
    queryFn: () => stats(),
    refetchInterval: 5000,
  });

  const rowsQ = useQuery({
    queryKey: ["feed-outbox", status],
    queryFn: () => list({ data: { status: status === "all" ? null : status } }),
    refetchInterval: 5000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["feed-stats"] });
    qc.invalidateQueries({ queryKey: ["feed-outbox"] });
    qc.invalidateQueries({ queryKey: ["outbound-events"] });
  };

  const backfillM = useMutation({
    mutationFn: () => backfill(),
    onSuccess: (r) => {
      toast.success(`Enqueued ${r.enqueued} submission${r.enqueued === 1 ? "" : "s"}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sendM = useMutation({
    mutationFn: () => runNow(),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Batch sent — ${r.sent} ok, ${r.failed} failed, ${r.dead} dead`);
      else toast.error(r.error ?? "Batch failed");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const retryM = useMutation({
    mutationFn: (s: "failed" | "dead") => retry({ data: { status: s } }),
    onSuccess: (r) => {
      toast.success(`Re-queued ${r.requeued} row${r.requeued === 1 ? "" : "s"}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const s = statsQ.data;
  const dead = s?.dead ?? 0;

  const tiles = [
    { label: "Queued", value: s?.queued ?? 0, tone: "" },
    { label: "Sending", value: s?.sending ?? 0, tone: "" },
    { label: "Sent today", value: s?.sent_today ?? 0, tone: "text-emerald-600" },
    { label: "Failed", value: s?.failed ?? 0, tone: (s?.failed ?? 0) > 0 ? "text-amber-600" : "" },
    { label: "Dead", value: dead, tone: dead > 0 ? "text-destructive" : "" },
  ];

  return (
    <>
      <div
        className={cn(
          "rounded-xl border bg-card p-5 shadow-sm",
          dead > 0 ? "border-destructive/60 ring-1 ring-destructive/30" : "border-border",
        )}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Creative submission feed</div>
            <div className="text-xs text-muted-foreground">
              Outbox pushing editor video submissions to LaunchPanda.
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Oldest queued: <span className="font-medium">{fmt(s?.oldest_queued_at)}</span>
          </div>
        </div>

        {dead > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>{dead} row{dead === 1 ? "" : "s"} never arrived.</strong> These exhausted all
              retries. Fix the cause, then use “Retry dead”.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-lg bg-muted/40 p-3">
              <div className="text-[11px] text-muted-foreground">{t.label}</div>
              <div className={cn("text-lg font-semibold tabular-nums", t.tone)}>{t.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => setConfirmOpen(true)} disabled={backfillM.isPending}>
            {backfillM.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send everything
          </Button>
          <Button variant="outline" onClick={() => sendM.mutate()} disabled={sendM.isPending}>
            {sendM.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Run sender now
          </Button>
          <Button
            variant="outline"
            onClick={() => retryM.mutate("failed")}
            disabled={retryM.isPending || (s?.failed ?? 0) === 0}
          >
            Retry failed
          </Button>
          <Button
            variant="destructive"
            onClick={() => retryM.mutate("dead")}
            disabled={retryM.isPending || dead === 0}
          >
            Retry dead
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="text-sm font-semibold">Outbox</div>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatus(st)}
                className={cn(
                  "h-7 rounded-full border px-3 text-[11px] font-medium capitalize transition-colors",
                  st === status
                    ? "border-transparent bg-foreground text-background"
                    : "border-border bg-background text-muted-foreground hover:bg-muted/50",
                )}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {rowsQ.isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading outbox…
          </div>
        ) : (rowsQ.data ?? []).length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No rows for this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Attempts</th>
                  <th className="px-4 py-2 font-medium">Next attempt</th>
                  <th className="px-4 py-2 font-medium">Last error</th>
                </tr>
              </thead>
              <tbody>
                {(rowsQ.data ?? []).map((r) => (
                  <tr
                    key={r.id}
                    className={cn(
                      "border-b border-border/60 last:border-0",
                      r.status === "dead" && "bg-destructive/10",
                    )}
                  >
                    <td className="whitespace-nowrap px-4 py-2">{fmt(r.created_at)}</td>
                    <td className="px-4 py-2">{r.event_type}</td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px] font-medium",
                          r.status === "sent" && "bg-emerald-500/15 text-emerald-600",
                          r.status === "dead" && "bg-destructive text-destructive-foreground",
                          r.status === "failed" && "bg-amber-500/15 text-amber-600",
                          (r.status === "queued" || r.status === "sending") &&
                            "bg-muted text-muted-foreground",
                        )}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 tabular-nums">
                      {r.attempts}/{r.max_attempts}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      {r.status === "sent" ? "—" : fmt(r.next_attempt_at)}
                    </td>
                    <td className="max-w-[380px] truncate px-4 py-2 text-muted-foreground">
                      {r.last_error ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send everything?</AlertDialogTitle>
            <AlertDialogDescription>
              This queues all {s?.total_submissions ?? 705} non-deleted creative submissions as
              upserts, oldest first. It is safe to run twice — the receiver skips anything it
              already has, and submissions already waiting in the outbox are not duplicated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => backfillM.mutate()}>
              Queue {s?.total_submissions ?? 705} rows
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
